import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react';
import { Content, ExitTicketActive, Lesson, Program, ExtendedQuiz } from '../types';
import { ExtendedHeaUnitFragment, ExtendedUnitFragment, QuizContent, SectionFragment, StudyPlan } from '@api/graphql-codegen';
export type SelectionLevel = 'Program' | 'Unit' | 'Lesson' | 'Content' | null;

type CourseContext = {
  content: undefined | Content;
  program: undefined | Program;
  lesson: undefined | Lesson;
  lessons: Lesson[];
  currentUnit: undefined | ExtendedUnitFragment;
  selectionLevel: SelectionLevel;
  setContent: (content: Content | undefined) => void;
  setProgram: (program: Program | undefined) => void;
  setLesson: (lesson: Lesson | undefined) => void;
  setLessons: (lessons: Lesson[]) => void;
  setCurrentUnit: (unit: ExtendedUnitFragment) => void;
  setSelectionLevel: (level: SelectionLevel) => void;
  clear: () => void;
  isParentApp?: boolean;
  setIsParent: (isParentApp: boolean) => void;
  isPreview?: boolean;
  setIsPreview: (isPreview: boolean) => void;
  pageNumber: number;
  setPageNumber: (pageNumber: number) => void;
  totalPages: number;
  setTotalPages: (totalPages: number) => void;
  lastContent: boolean;
  setLastContent: (lastContent: boolean) => void;
  currentHeaUnit: ExtendedHeaUnitFragment | undefined;
  setCurrentHeaUnit: (unit: ExtendedHeaUnitFragment) => void;
  accountLocked?: boolean;
  setAccountLocked: (accountLocked: boolean) => void;
  exitTickets: ExitTicketActive[];
  setExitTickets: (exitTickets: ExitTicketActive[]) => void;
  showExitModal?: boolean;
  setShowExitModal: (showExitModal: boolean) => void;
  currentStudyplan: undefined | StudyPlan;
  setCurrentStudyplan: (studyplan: StudyPlan) => void;
  showExitTicket?: boolean;
  setShowExitTicket: (showExitTicket: boolean) => void;
  confirmedPassword?: boolean;
  setConfirmedPassword: (confirmedPassword: boolean) => void;
  isViewExitTicket?: boolean;
  setIsViewExitTicket: (isViewExitTicket: boolean) => void;
  showReview?: boolean;
  setShowReview: (showReview: boolean) => void;
  showCompleteUnit?: boolean;
  setShowCompleteUnit: (showCompleteUnit: boolean) => void;
  isConfirmView?: boolean;
  setIsConfirmView: (isConfirmView: boolean) => void;
  section?: undefined | SectionFragment;
  setSection: (section: SectionFragment | undefined) => void;
  showExerciseModal?: boolean;
  setShowExerciseModal: (showExerciseModal: boolean) => void;
  startedAlternateLesson?: boolean;
  setStartedAlternateLesson: (startedAlternateLesson: boolean) => void;
  completeModal?: boolean;
  setCompleteModal: (completeModal: boolean) => void;
  isStudyGuide?: boolean;
  setIsStudyGuide: (isStudyGuide: boolean) => void;
  isStudentQuiz?: boolean;
  setIsStudentQuiz: (isStudentQuiz: boolean) => void;
  studentQuiz?: undefined | QuizContent;
  setStudentQuiz: (studentQuiz: QuizContent | undefined) => void;
  currentQuiz?: undefined | ExtendedQuiz;
  setCurrentQuiz: (currentQuiz: ExtendedQuiz | undefined) => void;
  myQuizzes: ExtendedQuiz[];
  setMyQuizzes: (myQuizzes: ExtendedQuiz[]) => void;
  isNewLessonLayout?: boolean;
  setIsNewLessonLayout: (isNewLessonLayout: boolean) => void;
};

const noop = () => {};
const defaultState: CourseContext = {
  content: undefined,
  setContent: noop,
  program: undefined,
  setProgram: noop,
  lesson: undefined,
  lessons: [],
  currentUnit: undefined,
  setLesson: noop,
  setLessons: noop,
  setCurrentUnit: noop,
  clear: noop,
  selectionLevel: null,
  setSelectionLevel: noop,
  isParentApp: false,
  setIsParent: noop,
  isPreview: false,
  setIsPreview: noop,
  pageNumber: 1,
  setPageNumber: noop,
  totalPages: 0,
  setTotalPages: noop,
  lastContent: false,
  setLastContent: noop,
  currentHeaUnit: undefined,
  setCurrentHeaUnit: noop,
  accountLocked: false,
  setAccountLocked: noop,
  exitTickets: [],
  setExitTickets: noop,
  showExitModal: false,
  setShowExitModal: noop,
  currentStudyplan: undefined,
  setCurrentStudyplan: noop,
  showExitTicket: false,
  setShowExitTicket: noop,
  confirmedPassword: false,
  setConfirmedPassword: noop,
  isViewExitTicket: false,
  setIsViewExitTicket: noop,
  showReview: false,
  setShowReview: noop,
  showCompleteUnit: false,
  setShowCompleteUnit: noop,
  isConfirmView: false,
  setIsConfirmView: noop,
  section: undefined,
  setSection: noop,
  showExerciseModal: false,
  setShowExerciseModal: noop,
  startedAlternateLesson: false,
  setStartedAlternateLesson: noop,
  completeModal: false,
  setCompleteModal: noop,
  isStudyGuide: false,
  setIsStudyGuide: noop,
  isStudentQuiz: false,
  setIsStudentQuiz: noop,
  studentQuiz: undefined,
  setStudentQuiz: noop,
  currentQuiz: undefined,
  setCurrentQuiz: noop,
  myQuizzes: [],
  setMyQuizzes: noop,
  isNewLessonLayout: false,
  setIsNewLessonLayout: noop,
};
const CourseContentContext = createContext(defaultState);

type Props = {
  children: ReactNode;
}

export const CourseContentProvider = ({ children }: Props) => {
  const [content, setContent] = useState<Content | undefined>(defaultState.content);
  const [program, setProgram] = useState<Program | undefined>(defaultState.program);
  const [lesson, setLesson] = useState<Lesson | undefined>(defaultState.lesson);
  const [lessons, setLessons] = useState<Lesson[]>(defaultState.lessons);
  const [currentUnit, setCurrentUnit] = useState<ExtendedUnitFragment | undefined>(defaultState.currentUnit);
  const [currentHeaUnit, setCurrentHeaUnit] = useState<ExtendedHeaUnitFragment | undefined>(defaultState.currentHeaUnit);
  const [isParentApp, setIsParent] = useState<boolean>(false);
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [selectionLevel, setSelectionLevel] = useState<SelectionLevel>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [lastContent, setLastContent] = useState<boolean>(false);
  const [accountLocked, setAccountLocked] = useState<boolean>(false);
  const [exitTickets, setExitTickets] = useState<ExitTicketActive[]>(defaultState.exitTickets);
  const [showExitModal, setShowExitModal] = useState<boolean>(false);
  const [currentStudyplan, setCurrentStudyplan] = useState<StudyPlan | undefined>();
  const [showExitTicket, setShowExitTicket] = useState<boolean>(false);
  const [confirmedPassword, setConfirmedPassword] = useState(false);
  const [isViewExitTicket, setIsViewExitTicket] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [showCompleteUnit, setShowCompleteUnit] = useState(false);
  const [isConfirmView, setIsConfirmView] = useState(false);
  const [section, setSection] = useState<SectionFragment | undefined>(defaultState.section);
  const [showExerciseModal, setShowExerciseModal] = useState(false);
  const [startedAlternateLesson, setStartedAlternateLesson] = useState(false);
  const [completeModal, setCompleteModal] = useState(false);
  const [isStudyGuide, setIsStudyGuide] = useState(false);
  const [isStudentQuiz, setIsStudentQuiz] = useState(false);
  const [studentQuiz, setStudentQuiz] = useState<QuizContent | undefined>(defaultState.studentQuiz)
  const [currentQuiz, setCurrentQuiz] = useState<ExtendedQuiz | undefined>(defaultState.currentQuiz)
  const [myQuizzes, setMyQuizzes] = useState<ExtendedQuiz[]>(defaultState.myQuizzes);
  const [isNewLessonLayout, setIsNewLessonLayout] = useState(false);

  const clear = useCallback(() => {
    setContent(undefined);
    setProgram(undefined);
    setLesson(undefined);
    setCurrentUnit(undefined);
  }, []);

  return (
    <CourseContentContext.Provider value={{
      content,
      program,
      lesson,
      lessons,
      setContent,
      setProgram,
      setLesson,
      setLessons,
      currentUnit,
      setCurrentUnit,
      clear,
      selectionLevel,
      setSelectionLevel,
      isParentApp,
      setIsParent,
      isPreview,
      setIsPreview,
      pageNumber,
      setPageNumber,
      totalPages,
      setTotalPages,
      lastContent,
      setLastContent,
      currentHeaUnit,
      setCurrentHeaUnit,
      accountLocked,
      setAccountLocked,
      exitTickets,
      setExitTickets,
      showExitModal,
      setShowExitModal,
      currentStudyplan,
      setCurrentStudyplan,
      showExitTicket,
      setShowExitTicket,
      confirmedPassword,
      setConfirmedPassword,
      isViewExitTicket,
      setIsViewExitTicket,
      showReview,
      setShowReview,
      showCompleteUnit,
      setShowCompleteUnit,
      isConfirmView,
      setIsConfirmView,
      section,
      setSection,
      showExerciseModal,
      setShowExerciseModal,
      startedAlternateLesson,
      setStartedAlternateLesson,
      completeModal,
      setCompleteModal,
      isStudyGuide,
      setIsStudyGuide,
      isStudentQuiz,
      setIsStudentQuiz,
      studentQuiz,
      setStudentQuiz,
      currentQuiz,
      setCurrentQuiz,
      myQuizzes,
      setMyQuizzes,
      isNewLessonLayout,
      setIsNewLessonLayout,
    }}>
      {children}
    </CourseContentContext.Provider>
  );
}

export const useCourseContent = () => useContext(CourseContentContext);
